import React from "react";
import { RefreshCw, Trash2 } from "lucide-react";
import type { IntegrationLinkDto, IntegrationTargetType } from "@/api/integrations/links";
import type { ProviderState, SourceRow } from "./useIntegrationsData";

const GRID = "minmax(190px, 2fr) minmax(150px, 1.3fr) minmax(105px, 1fr) minmax(105px, 1fr) minmax(105px, 1fr) 110px";

interface TableProps {
    state: ProviderState;
    targetType?: IntegrationTargetType;
    targetNameById?: ReadonlyMap<string, string>;
    /** Legacy Product-only alias retained for compatibility. */
    productNameById?: ReadonlyMap<number, string>;
    syncingIds: ReadonlySet<string | number>;
    pendingUnlinkKey?: string | null;
    pendingUnlinkId?: number | null;
    onSync: (row: SourceRow, link: IntegrationLinkDto) => void;
    onUnlink: (row: SourceRow, link: IntegrationLinkDto) => void;
}

interface TargetLinkRow {
    source: SourceRow;
    link: IntegrationLinkDto;
}

export function linkTargetType(link: IntegrationLinkDto): IntegrationTargetType {
    return link.target?.type ?? "product";
}

export function linkTargetId(link: IntegrationLinkDto): string {
    return link.target?.id ?? String(link.projectId ?? "");
}

export function linkKey(link: IntegrationLinkDto): string {
    return `${linkTargetType(link)}:${linkTargetId(link)}:${String(link.id)}`;
}

export function connectedTargetRows(rows: SourceRow[], targetType: IntegrationTargetType): TargetLinkRow[] {
    return rows.flatMap(source => source.links
        .filter(link => linkTargetType(link) === targetType)
        .map(link => ({ source, link })));
}

export function connectedProductRows(rows: SourceRow[]): TargetLinkRow[] {
    return connectedTargetRows(rows, "product");
}

export function formatRelativeTime(timestamp: string | null, now = Date.now()): string {
    if (!timestamp) return "Never synced";
    const parsed = Date.parse(timestamp);
    if (!Number.isFinite(parsed)) return "—";
    const elapsedSeconds = Math.max(0, Math.floor((now - parsed) / 1000));
    if (elapsedSeconds < 60) return "Just now";

    const intervals: Array<[number, string]> = [
        [31_536_000, "year"],
        [2_592_000, "month"],
        [604_800, "week"],
        [86_400, "day"],
        [3_600, "hour"],
        [60, "minute"]
    ];
    for (const [seconds, unit] of intervals) {
        if (elapsedSeconds >= seconds) {
            const value = Math.floor(elapsedSeconds / seconds);
            return `${value} ${unit}${value === 1 ? "" : "s"} ago`;
        }
    }
    return "Just now";
}

function projectLabel(state: ProviderState, row: SourceRow, link: IntegrationLinkDto): string {
    if (state.meta.id === "azure-devops") {
        const organization = state.organization || state.siteOrOrg;
        return organization ? `${row.externalName} · ${organization}` : row.externalName;
    }
    const linkedBranch = typeof link.metadata?.branch === "string"
        ? link.metadata.branch.trim()
        : "";
    const identifier = state.meta.kind === "git"
        ? (linkedBranch || row.keyOrBranch)
        : row.keyOrBranch;
    return identifier ? `${row.externalName} · ${identifier}` : row.externalName;
}

function syncData(link: IntegrationLinkDto): string {
    if (link.syncedCount == null || !link.syncedUnit) return "Not synced";
    return `${link.syncedCount.toLocaleString()} ${link.syncedUnit}`;
}

function statusDetails(link: IntegrationLinkDto, syncing: boolean) {
    if (syncing) return { label: "Syncing…", color: "var(--primary)" };
    switch (link.syncStatus) {
        case "synced": return { label: "Synced", color: "var(--success)" };
        case "failed": return { label: "Sync failed", color: "var(--error)" };
        default: return { label: "Needs review", color: "#d97706" };
    }
}

function LinkRow({ state, source, link, targetType = "product", targetNameById, productNameById, syncingIds, pendingUnlinkKey, pendingUnlinkId, onSync, onUnlink }: TableProps & TargetLinkRow) {
    const targetId = linkTargetId(link);
    const fallback = targetType === "product" ? `Product ${targetId}` : `Context Mesh ${targetId}`;
    const targetName = link.target?.name || targetNameById?.get(targetId) ||
        (targetType === "product" ? productNameById?.get(Number(targetId)) : undefined) || fallback;
    const key = linkKey(link);
    const syncing = syncingIds.has(key) || syncingIds.has(link.id);
    const pendingRemoval = pendingUnlinkKey === key || pendingUnlinkId === link.id;
    const status = statusDetails(link, syncing);
    const sourceLabel = projectLabel(state, source, link);

    return (
        <div className="il-product-row" style={{ gridTemplateColumns: GRID }}>
            <div className="il-product-cell" title={targetName}>
                <span className="il-product-initial">{(targetName.trim()[0] || "?").toUpperCase()}</span>
                <span className="il-product-name">{targetName}</span>
            </div>
            <div className="il-source-label" title={sourceLabel}>{sourceLabel}</div>
            <div className="il-status" role="status" aria-live="polite" title={link.lastSyncError || undefined}>
                <span className="il-status-dot" style={{ background: status.color }} />
                <span style={{ color: status.color }}>{status.label}</span>
            </div>
            <span className="il-cell-text">{formatRelativeTime(link.lastSyncedAt)}</span>
            <span className="il-cell-text">{syncData(link)}</span>
            <div className="il-row-actions">
                <button
                    type="button"
                    className="il-icon-button"
                    aria-label={`Sync ${targetName} now`}
                    style={{ width: 44, height: 44 }}
                    disabled={syncing || pendingRemoval}
                    onClick={() => onSync(source, link)}
                >
                    <RefreshCw className={syncing ? "il-spin" : undefined} size={16} strokeWidth={1.9} />
                </button>
                <button
                    type="button"
                    className="il-icon-button il-icon-button-danger"
                    aria-label={`Remove ${targetName} connection`}
                    style={{ width: 44, height: 44 }}
                    disabled={syncing || pendingRemoval}
                    onClick={() => onUnlink(source, link)}
                >
                    <Trash2 size={16} strokeWidth={1.9} />
                </button>
            </div>
        </div>
    );
}

export function LinkTable(props: TableProps) {
    const targetType = props.targetType ?? "product";
    const targetRows = connectedTargetRows(props.state.rows, targetType);
    const targetLabel = targetType === "product" ? "products" : "Context Meshes";
    if (targetRows.length === 0) {
        return <div className="il-empty-state">No {targetLabel} connected yet.</div>;
    }

    return (
        <div className="il-link-table">
            <div className="il-table-header" style={{ gridTemplateColumns: GRID }}>
                <span className="il-colhead">{targetType === "product" ? "Product" : "Context Mesh"}</span>
                <span className="il-colhead">{props.state.meta.connectedColumnLabel}</span>
                <span className="il-colhead">Status</span>
                <span className="il-colhead">Last Synced</span>
                <span className="il-colhead">Data Synced</span>
                <span className="il-colhead" style={{ textAlign: "right" }}>Actions</span>
            </div>
            {targetRows.map(({ source, link }) => (
                <LinkRow key={linkKey(link)} {...props} source={source} link={link} />
            ))}
        </div>
    );
}
